class MMBundleBuilder extends HTMLElement {
  constructor() {
    super();

    const jsonText = document.querySelector('.js-bundle-products-json')?.textContent;
    this.bundleProductsJson = jsonText ? JSON.parse(jsonText) : null;
  }

  connectedCallback() {
    this.init();

    theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, (e) => {
      this.render(e.cart);
    });
  }

  async init() {
    try {
      const response = await fetch('/cart.js');
      if (!response.ok) throw new Error('Failed to fetch cart');
      const cart = await response.json();
      this.render(cart);
    } catch (error) {
      console.error('Error initializing bundle builder:', error);
    }
  }

  async render(cart) {
    const promotionItems = document.querySelectorAll('mm-bundle-builder .js-promotion-item');
    if (!promotionItems.length || !cart) return;

    const thresholds = Array.from(promotionItems).map(item => ({
      element: item,
      threshold: parseInt(item.dataset.threshold) || 0,
      productId: item.dataset.productId || null,
      discountCode: item.dataset.discountCode || null,
      wasReached: item.classList.contains('reached')
    })).sort((a, b) => a.threshold - b.threshold);

    const totalPromoQty = await this.handleQty(cart);
    promotionItems.forEach(item => item.classList.remove("reached"));

    let reachedCount = 0;
    const productsToAdd = [];
    const productsToRemove = [];
    const discountsToApply = [];
    const discountsToRemove = [];

    let highestReachedTier = null;
    for (let i = thresholds.length - 1; i >= 0; i--) {
      if (totalPromoQty >= thresholds[i].threshold) {
        highestReachedTier = thresholds[i];
        break;
      }
    }

    thresholds.forEach(tier => {
      const isReached = totalPromoQty >= tier.threshold;
      const isHighestReached = highestReachedTier && tier === highestReachedTier;

      if (isReached) {
        tier.element.classList.add("reached");
        reachedCount++;
      } else {
        tier.element.classList.remove("reached");
      }

      if (isHighestReached) {
        if (tier.productId && !tier.wasReached) {
          productsToAdd.push(tier.productId);
        }
        if (tier.discountCode) {
          discountsToApply.push(tier.discountCode);
        }
      } else {
        if (tier.productId && tier.wasReached) {
          productsToRemove.push(tier.productId);
        }
        if (tier.discountCode) {
          discountsToRemove.push(tier.discountCode);
        }
      }
    });

    const currentDiscountCodes = cart.discount_codes ? cart.discount_codes.map(dc => dc.code) : [];
    discountsToRemove.forEach(code => {
      if (currentDiscountCodes.includes(code)) {
        this.removeDiscountCode(code, cart);
      }
    });

    discountsToApply.forEach(code => {
      if (!currentDiscountCodes.includes(code)) {
        this.applyDiscountCode(code, cart);
      }
    });

    productsToAdd.forEach(variantId => this.addProduct(variantId, cart));
    productsToRemove.forEach(variantId => this.removeProduct(variantId));

    this.handleProgressBar(thresholds, totalPromoQty);
  }

  async applyDiscountCode(code, cart) {
    if (cart.discount_codes.some(dc => dc.code === code)) return;

    const formData = new FormData();
    formData.append('discount', code);

    try {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        body: formData
      });
      await response.json();
      document.dispatchEvent(new CustomEvent('cart:refresh'));
    } catch (error) {
      console.error('Error applying discount code:', error);
    }
  }

  async removeDiscountCode(code, cart) {
    const appliedCodes = cart.discount_codes.map(dc => dc.code);
    if (!appliedCodes.includes(code)) return;

    const remainingCodes = appliedCodes.filter(dc => dc !== code);
    const formData = new FormData();
    formData.append('discount', remainingCodes.join(','));

    try {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        body: formData
      });
      await response.json();
      document.dispatchEvent(new CustomEvent('cart:refresh'));
    } catch (error) {
      console.error('Error removing discount code:', error);
    }
  }


  removeProduct(variantId) {
    const cartItems = document.querySelector('cart-items');
    if (!cartItems) return;

    cartItems.querySelectorAll('ul>li').forEach(item => {
      const itemVariantId = item.getAttribute('data-variant-id');

      if (itemVariantId == variantId) {
        cartItems.updateQuantity(item.getAttribute('data-key-index'), 0);
      }
    });
  }

  addProduct(variantId, cart) {
    const isInCart = cart.items.some(
      item => item.variant_id == variantId && item.properties?._promotion
    );
    if (isInCart) return;

    let sectionsToBundle = ['mm-cart-drawer'];
    document.documentElement.dispatchEvent(new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections: sectionsToBundle } }));
    const config = theme.utils.fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', 1);
    formData.append('properties[_promotion]', 'true');
    formData.append('sections', sectionsToBundle);
    formData.append('sections_url', window.location.pathname);
    config.body = formData;

    fetch(theme.routes.cart_add_url, config)
      .then((response) => response.json())
      .then(async (parsedState) => {
        if (parsedState.status) {
          theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartError, {
            source: 'product-form',
            productVariantId: formData.get('id'),
            errors: parsedState.errors || parsedState.description,
            message: parsedState.message
          });
          document.dispatchEvent(new CustomEvent('ajaxProduct:error', {
            detail: {
              errorMessage: parsedState.description
            }
          }));

          const cartJson = await (await fetch(theme.routes.cart_url, { ...theme.utils.fetchConfig('json', 'GET')})).json();
          theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'product-form', productVariantId: formData.get('id'), cart: cartJson });

          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { open: false } }));
          return;
        }

        const cartJson = await (await fetch(theme.routes.cart_url, { ...theme.utils.fetchConfig('json', 'GET')})).json();
        cartJson['sections'] = parsedState['sections'];

        theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'mm-product-form', productVariantId: formData.get('id'), cart: cartJson });
        document.dispatchEvent(new CustomEvent('ajaxProduct:added', {
          detail: {
            product: parsedState
          }
        }));

        this.cartDrawer?.show(this.activeElement);
      })
      .catch((error) => {
        console.log(error);
      });

  }

  handleProgressBar(thresholds, totalPromoQty) {
    const progressFill = document.getElementById('bundle-progress-bar');
    if (!progressFill) return;

    const maxThreshold = thresholds[thresholds.length - 1].threshold;
    const percentage = Math.min((totalPromoQty / maxThreshold) * 100, 100);

    progressFill.style.width = `${percentage}%`;
  }

  async handleQty(cart) {
    if (!this.bundleProductsJson || !cart) return 0;

    const collectionVariantIds = new Set();
    this.bundleProductsJson.forEach(product => {
      product.variants.forEach(variant => {
        collectionVariantIds.add(Number(variant.id));
      });
    });

    let totalCount = 0;
    cart.items.forEach(item => {
      const variantIdStr = item.variant_id;
      if (collectionVariantIds.has(variantIdStr)) {
        totalCount += item.quantity;
      }
    });

    return totalCount;
  }
}

customElements.define('mm-bundle-builder', MMBundleBuilder);


(() => {
  let lastScrollTop = 0;
  const colWrapper = document.querySelector('.col-wrapper');
  if(!colWrapper) return;

  window.addEventListener('scroll', function() {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    if (scrollTop >= lastScrollTop) {
      if(!colWrapper.classList.contains('is-scrolling-down')) {
        colWrapper.classList.add('is-scrolling-down');
      }
    } else {
      if(colWrapper.classList.contains('is-scrolling-down')) {
        colWrapper.classList.remove('is-scrolling-down');
      }
    }

    lastScrollTop = scrollTop <= 0 ? 0 : scrollTop;
  }, false);
})();